import { ESCAPE } from '@angular/cdk/keycodes';
import { GlobalPositionStrategy, Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector, Type, inject } from '@angular/core';
import { Subject, delay, filter, map, race, take, tap } from 'rxjs';
import { DialogContainerComponent } from './dialog.container/dialog.container.component';
import Enumerable from 'linq';

export interface DialogConfig<T = any> {

    /** Whether the dialog has a backdrop. */
    hasBackdrop?: boolean;

    closeOnBackdropClick?: boolean;
    closeOnEscape?: boolean;

    /** CSS class to be added to the dialog backdrop. */
    backdropClass?: string[];

    /** CSS class to be added to the dialog panel. */
    panelClass?: string[];

    /** Top position of the dialog */
    top?: string;

    /** Width of the dialog */
    width?: string;

    /** Height of the dialog */
    height?: string;

    /** Max Width of the dialog */
    maxWidth?: string;

    /** Max Height of the dialog */
    maxHeight?: string;

    /** Allows you to pass inputs to the dialog component */
    inputs?: Record<string, any>;

    /** No supported yet */
    outputs?: Record<string, any>;

    /** Allows passing data that can be used in the dialog component */
    data?: T;

    /** This is set by the service and is used to close the dialog */
    close?: (data?: any) => void;
}

export interface DialogConfigExtended extends DialogConfig {
    componentType: Type<any>;
    injector?: Injector;
    mode?: 'side' | 'modal' | 'pseudo';
}

export const DIALOG_CONFIG = new InjectionToken<DialogConfigExtended>('SIDE_DIALOG_CONFIG');


@Injectable({ providedIn: 'root' })
export class WorkBuddySideDialogService {

    private injector = inject(Injector);
    private overlay = inject(Overlay);


    public openSideDialog<T = any>(componentType: Type<any>, dialogConfig?: DialogConfig): Promise<T> {
        return this.open(componentType, { ...dialogConfig, mode: 'side' } as DialogConfigExtended);
    }

    public openDialog<T = any>(componentType: Type<any>, dialogConfig?: DialogConfig): Promise<T> {
        const config = { ...dialogConfig, mode: 'modal' } as DialogConfigExtended;
        return this.open(componentType, config);
    }

    public openPseudoPage<T = any>(componentType: Type<any>, dialogConfig?: DialogConfig): Promise<T> {
        const config = { ...dialogConfig, mode: 'pseudo' } as DialogConfigExtended;
        return this.open(componentType, config);
    }

    private open(componentType: Type<any>, dialogConfig?: DialogConfigExtended): Promise<any> {

        return new Promise((resolve, reject) => {

            dialogConfig = dialogConfig || {} as DialogConfigExtended;

            const sideDialog = dialogConfig.mode === 'side' || false;
            const panelClass = dialogConfig?.panelClass || [];
            const backdropClass = dialogConfig?.backdropClass || [];
            const hasBackdrop = dialogConfig?.hasBackdrop || true;
            const closeDelay = sideDialog ? 200 : 100;
            const scrollStrategy = this.overlay.scrollStrategies.block();

            const width = sideDialog
                ? dialogConfig?.width || '450px'
                : dialogConfig?.width || 'auto';

            const height = sideDialog
                ? '100%'
                : dialogConfig?.height ?? 'auto';

            const maxHeight = sideDialog
                ? 'unset'
                : dialogConfig?.maxHeight ? `calc(${dialogConfig.maxHeight} - 100px)` : `calc(100% - 200px)`;

            const maxWidth = sideDialog
                ? 'unset'
                : dialogConfig?.maxWidth ?? 'unset';

            let config: OverlayConfig = {
                hasBackdrop: hasBackdrop,
                backdropClass: sideDialog
                    ? ['w-dialog-backdrop', 'w-side-dialog-backdrop', ...panelClass]
                    : ['w-dialog-backdrop', 'w-modal-dialog-backdrop', ...backdropClass],
                panelClass: sideDialog
                    ? ['w-dialog-panel', 'w-side-dialog-panel', ...backdropClass]
                    : ['w-dialog-panel', 'w-modal-dialog-panel', ...panelClass],
                width,
                height,
                maxHeight,
                maxWidth,
                scrollStrategy,
            }

            let positionStrategy: GlobalPositionStrategy = null;

            switch (dialogConfig.mode) {
                case 'modal':
                    positionStrategy = this.overlay
                        .position()
                        .global()
                        .centerHorizontally()
                        .top('100px');
                    break;

                case 'side':
                    positionStrategy = this.overlay
                        .position()
                        .global()
                        .right('0');
                    break;

                case 'pseudo':

                    const sideNav = Enumerable.from(document.getElementsByTagName('side-nav')).firstOrDefault();
                    const sideNavWidth = sideNav?.clientWidth + 30;

                    positionStrategy = this.overlay
                        .position()
                        .global()
                        .left(`${sideNavWidth}px`)
                        .right('10px')
                        .top('10px');


                    config = {
                        ...config,
                        positionStrategy,
                        width: `calc(100% - ${sideNavWidth}px)`,
                        height: 'calc(100% - 20px)',
                    }

                    break;
            }


            config = { ...config, positionStrategy };

            const portalOutlet = this.overlay.create(config);

            const userClose$ = new Subject<any>();
            const backdropClose$ = portalOutlet.backdropClick()
                .pipe(
                    filter(_ => dialogConfig.closeOnBackdropClick),
                    map(x => null)
                );

            const escapeClose$ = portalOutlet.keydownEvents()
                .pipe(
                    filter(_ => dialogConfig.closeOnEscape),
                    filter(event => (event as KeyboardEvent).keyCode === ESCAPE)
                );

            race(userClose$, backdropClose$, escapeClose$)
                .pipe(
                    take(1),
                    tap(_ => {
                        portalOutlet.backdropElement.classList.add('w-side-dialog-closing');
                        portalOutlet.overlayElement.classList.add('w-side-dialog-closing');
                    }),
                    delay(closeDelay)
                )
                .subscribe(data => {
                    portalOutlet.detach();
                    portalOutlet.dispose();
                    resolve(data);
                });

            dialogConfig.close = (data?: any) => { userClose$.next(data) };

            const injector = Injector.create({
                providers: [
                    {
                        provide: DIALOG_CONFIG,
                        useValue: {
                            ...dialogConfig,
                            componentType: componentType,
                        },
                    }
                ],
                parent: this.injector
            });

            const componentPortal = new ComponentPortal(DialogContainerComponent, null, injector);
            portalOutlet.attach(componentPortal);

        });


    }
}